// @flow strict

import React from 'react';
import ReactDOM from 'react-dom';
import {
  BrowserRouter as Router,
  Link,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { registerAll } from '@nwea/nwea-frontend-components-v1';
import '@nwea/nwea-frontend-components-v1/dist/design-system.css';

import { type State, getStateList } from './state-list.js';
import './reset.css';
import './user-feedback.css';
import appStyles from './main.css';
import buttonStyles from './button.css';
import { TopBar } from './top-bar.jsx';
import { SelectPage } from './select-page.jsx';
import { type SkillsPageProps, default as SkillsPage } from './SkillsPage';
import arrowForwardSvg from './images/ic_arrow_forward_24px.svg';
import svgImageFn from './svg-image.jsx';
import iconStyles from './icons.css';
import UrlParamHandler from './url-param-handler.jsx';
import { BottomBar } from './bottom-bar.jsx';
import './analytics.js';
import './user-feedback.js';
import './new-relic.js';

registerAll({
  cssFile: './vendor.css',
});

// we don't supply an icon style here so the button style can control the fill for the pseudo selectors
const ArrowForwardIcon = svgImageFn(
  iconStyles.mediumSquare,
  '',
  arrowForwardSvg
);

const StartPage = () => {
  // TODO: pass in route name?
  return (
    <main className={appStyles.mainColumn}>
      <h1 className="nwea-ds-v1-typography-heading-1" data-cy="heading">
        Welcome to the Achievement Level Explorer!
      </h1>
      <div>
        <p className="nwea-ds-v1-typography-heading-5" data-cy="how-to-heading">
          With this resource, you will be able to:
        </p>
        <ul className={appStyles.withPurpleDot}>
          <li
            className="nwea-ds-v1-typography-body-copy-medium"
            data-cy="list-item01"
            style={{ margin: 0 }}
          >
            access the progression of grade-level performance expectations defined by your state&apos;s standards-based achievement level descriptors,
          </li>
          <li
            className="nwea-ds-v1-typography-body-copy-medium"
            data-cy="list-item02"
            style={{ margin: 0 }}
          >
            analyze student performance by achievement level,
          </li>
          <li
            className="nwea-ds-v1-typography-body-copy-medium"
            data-cy="list-item03"
            style={{ margin: 0 }}
          >
            plan instruction so all students have the opportunity to learn and meet the expectations for their highest levels of achievement, and
          </li>
          <li
            className="nwea-ds-v1-typography-body-copy-medium"
            data-cy="list-item04"
            style={{ margin: 0 }}
          >
            analyze student progress and identify target standards for growth.
          </li>
        </ul>
      </div>
      <Link
        to="/pick-options"
        className={buttonStyles.purpleLink}
        data-cy="next-button"
      >
        Let&apos;s Get Started <ArrowForwardIcon />
      </Link>
    </main>
  );
};

type AppProps = {||};
// TODO: write a more "refined" type - i.e. to exclude values like {selectedPartnerId: null, selectedGradeId: 'a string', selectedSubjectId: 'another string'}
type AppState = {
  selectedPartnerId: ?string,
  selectedGradeId: ?string,
  selectedSubjectId: ?string,
  stateList: State[],
};

const HistoryWrappedSkillsPage = (props: SkillsPageProps) => {
  const location = useLocation();

  // history.location.state was stored in SelectPage's Link or UrlParamHandler. Any cleaner way to organize this code?
  const partnerId =
    props.selectedPartner
    || (location.state ? location.state.selectedPartnerId : null);
  const gradeId =
    props.selectedGradeId
    || (location.state ? location.state.selectedGradeId : null);
  const subjectId =
    props.selectedSubjectId
    || (location.state ? location.state.selectedSubjectId : null);
  const achievementLevel =
    props.selectedAchievementLevel
    || (location.state && location.state.selectedAchievementLevel
        ? location.state.selectedAchievementLevel
        : null);
  return (
    <SkillsPage
      stateList={props.stateList}
      selectedPartner={partnerId}
      selectedGradeId={gradeId}
      selectedSubjectId={subjectId}
      selectedAchievementLevel={achievementLevel}
      onDataRefresh={props.onDataRefresh}
    />
  );
};

class App extends React.Component<AppProps, AppState> {
  constructor(props) {
    super(props);
    this.state = {
      selectedPartnerId: null,
      selectedGradeId: null,
      selectedSubjectId: null,
      stateList: [],
    };
  }
  componentDidMount() {
    getStateList()
      .then((stateList) => {
        this.setState({ stateList });
      })
      .catch((e) => console.error(e)); // TODO: Error page, see https://nwea.tpondemand.com/entity/443539-ald-error-page
  }
  // Note: this method style on classes is experimental and enabled by '@babel/plugin-proposal-class-properties'
  onOptionsSelection = (partnerId, gradeId, subjectId) => {
    this.setState({
      selectedPartnerId: partnerId,
      selectedGradeId: gradeId,
      selectedSubjectId: subjectId,
    });
  };
  render() {
    return (
      <>
        <div className={appStyles.app}>
          <header>
            <TopBar />
          </header>
          <Router>
            <UrlParamHandler />
            <Routes>
              <Route exact path="/" element={ <StartPage /> } />
              <Route path="/pick-options" element={
                <SelectPage
                  data={this.state.stateList}
                  onOptionsSelection={this.onOptionsSelection}
                />
              } />
              <Route path="/display-skills" element={
                <HistoryWrappedSkillsPage
                  stateList={this.state.stateList}
                  selectedPartner={this.state.selectedPartnerId}
                  selectedGradeId={this.state.selectedGradeId}
                  selectedSubjectId={this.state.selectedSubjectId}
                  selectedAchievementLevel={null} // only settable via query parameters for now
                  onDataRefresh={this.onOptionsSelection}
                />
              } />
            </Routes>
          </Router>
        </div>
        <footer>
          <BottomBar />
        </footer>
      </>
    );
  }
}

// eslint-disable-next-line no-undef
const rootElement = document.getElementById('root');
if (rootElement) {
  ReactDOM.render(<App />, rootElement);
}
